/* Paid add-ons on top of Pro or Team. First one: Elternportal (parent page + cancellations by parents).
   Bought by the owner as its own monthly subscription; the webhook writes org_addons, my_context() returns them. */
import { CLOUD, loggedIn, sbRefresh, sbUrl } from './api.js';
import { ctx, isFree, openPortal, openUpgrade, plan } from './plan.js';
import { cloudCheck } from './sync.js';
import { t } from '../i18n/index.js';
import { S, persist } from '../state.js';
import { IC, closeSheet, esc, render, sheet, toast } from '../ui.js';
import { money } from '../dates.js';

/* monthly prices shown in the app — keep them equal to the Stripe prices */
export const ADDON_PRICE={parents:{pro:3,team:15}};

export const hasAddon=a=>{const c=ctx();return !!(loggedIn()&&c&&Array.isArray(c.addons)&&c.addons.includes(a))};
const isTeamPlan=()=>['team','team_plus','enterprise'].includes(plan());
const isOwner=()=>{const c=ctx();return !c||!c.role||c.role==='owner'};
export const parentsCfg=()=>({invite:true,report:true,cancel:true,lateBill:true,makeup:true,...(S.settings.parents||{})});

/* returns an error text for the sheet, or redirects to Stripe */
async function addonCheckout(name){
  try{
    if(Date.now()>CLOUD.auth.exp)await sbRefresh();
    const r=await fetch(sbUrl('/functions/v1/billing/addon'),{method:'POST',
      headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.auth.at,'Content-Type':'application/json'},
      body:JSON.stringify({addon:name,org:CLOUD.org,return_url:location.origin+location.pathname})});
    const d=await r.json().catch(()=>({}));
    if(r.ok&&d.url){location.href=d.url;return ''}
    const e=String(d.error||'');
    console.error('addon checkout',r.status,e);
    return r.status===404?t('addon.e.deploy'):/price not configured|STRIPE_SECRET_KEY/.test(e)?t('addon.e.price'):e==='forbidden'?t('addon.ownerOnly'):/already active/.test(e)?t('addon.active'):/paid plan/.test(e)?t('addon.needsPlan'):t('plan.err')+(e?' ('+e.slice(0,80)+')':'');
  }catch(e){console.error(e);return t('plan.err')}
}

/* the sales sheet: what it does, what it costs, one button */
export function openAddonSheet(name='parents'){
  const price=ADDON_PRICE[name][isTeamPlan()?'team':'pro'];
  const owner=isOwner(),free=isFree();
  const b=sheet(`<div class="sheethead"><h2>${t('addon.parents.title')}</h2><button class="iconbtn" data-x aria-label="${esc(t('c.close'))}">${IC.x}</button></div>
  <div class="sheetbody addonsheet">
    <p class="ao-lead">${t('addon.parents.lead')}</p>
    <ul class="checklist">${['f1','f2','f6','f3','f4','f5'].map(k=>'<li>'+t('addon.parents.'+k)+'</li>').join('')}</ul>
    <div class="ao-price"><b>${esc(t('addon.price',{p:money(price)}))}</b><small>${t(isTeamPlan()?'addon.forSchool':'addon.forYou')}</small></div>
    ${!owner?`<p class="hint">${t('addon.ownerOnly')}</p>`:free?`<p class="hint">${t('addon.needsPlan')}</p>`:`<p class="hint">${t('addon.trial')}</p>`}
    <p class="ao-err" role="alert" hidden></p>
  </div>
  <div class="sheetfoot"><div class="left"></div><button class="btn" data-x>${t('c.close')}</button>
    ${owner?(free?`<button class="btn primary" data-up>${IC.bolt} ${t('plans.title')}</button>`:`<button class="btn primary" data-buy>${t('addon.try')}</button>`):''}</div>`);
  b.querySelectorAll('[data-x]').forEach(x=>x.onclick=()=>closeSheet(b));
  const up=b.querySelector('[data-up]');if(up)up.onclick=()=>{closeSheet(b);openUpgrade()};
  const buy=b.querySelector('[data-buy]');
  if(buy)buy.onclick=async()=>{
    const lbl=buy.textContent;buy.disabled=true;buy.textContent=t('plan.redirect');
    const err=await addonCheckout(name);
    if(err){buy.disabled=false;buy.textContent=lbl;const p=b.querySelector('.ao-err');p.textContent=err;p.hidden=false}
  };
}

/* back from Stripe: wait for the webhook, then say so */
export function handleAddonReturn(){
  const q=new URLSearchParams(location.search);
  if(q.get('checkout')!=='success'||!q.get('addon'))return false;
  try{history.replaceState(null,'',location.pathname)}catch(e){}
  let n=0;
  const tick=async()=>{await cloudCheck(true);if(hasAddon('parents')){render();toast(t('addon.active'));import('./parents.js').then(m=>m.syncParents(true));return}if(++n<8)setTimeout(tick,2000)};
  setTimeout(tick,1500);
  return true;
}

/* settings → plan: the add-on and its rules */
export function addonsCard(){
  if(!loggedIn()||!ctx())return '';
  const on=hasAddon('parents'),cfg=parentsCfg(),lateH=+(S.settings.lateCancelH??24);
  return `<div class="card mt addoncard"><h3 class="cardsub">${t('addon.title')}</h3>
    <div class="setrow"><span class="planic${on?'':' free'}">${PARENT_IC}</span><div class="grow"><b>${t('addon.parents.title')}</b>
      <small>${on?t('addon.on'):t('addon.price',{p:money(ADDON_PRICE.parents[isTeamPlan()?'team':'pro'])})}</small></div>
      <div class="planbtns">${on?`<button class="btn small" data-go="parents">${t('pp.open')}</button>`+(isOwner()?`<button class="btn small ghost" data-aoportal>${t('plan.manage')}</button>`:''):`<button class="btn small primary" data-aoopen>${t('addon.more')}</button>`}</div></div>
 ${on?`<div class="ao-rules">
      <label class="check"><input type="checkbox" data-aopar="invite" ${cfg.invite?'checked':''}> ${t('addon.rule.invite')}</label>
      <label class="check"><input type="checkbox" data-aopar="report" ${cfg.report?'checked':''}> ${t('addon.rule.report')}</label>
      <label class="check"><input type="checkbox" data-aopar="cancel" ${cfg.cancel?'checked':''}> ${t('addon.rule.cancel')}</label>
      <label class="check"><input type="checkbox" data-aopar="lateBill" ${cfg.lateBill?'checked':''}${cfg.cancel?'':' disabled'}> ${t('addon.rule.lateBill',{h:lateH})}</label>
      <label class="check"><input type="checkbox" data-aopar="makeup" ${cfg.makeup?'checked':''}> ${t('addon.rule.makeup')}</label>
      <p class="hint">${t('addon.rule.hint')}</p></div>`:''}
  </div>`;
}
export function bindAddons(m){
  m.querySelectorAll('[data-aoopen]').forEach(b=>b.onclick=()=>openAddonSheet('parents'));
  m.querySelectorAll('[data-aoportal]').forEach(b=>b.onclick=()=>openPortal());
  m.querySelectorAll('[data-aopar]').forEach(c=>c.onchange=()=>{
    S.settings.parents={...parentsCfg(),[c.dataset.aopar]:c.checked};persist();render();
    if(c.dataset.aopar==='invite'&&c.checked)setTimeout(()=>import('./parents.js').then(m=>m.syncParents(true)),2500);
  });
}
export const PARENT_IC='<svg viewBox="0 0 24 24"><circle cx="8" cy="7.5" r="3"/><circle cx="16.5" cy="9" r="2.3"/><path d="M2.8 19.5c.6-3.3 2.6-5.4 5.2-5.4s4.6 2.1 5.2 5.4M13.6 15.2c.8-.7 1.8-1.1 2.9-1.1 2.2 0 3.8 1.7 4.3 4.4"/></svg>';

/* settings → teaching: weekly free times; parents book make-up lessons inside them */
const DOW=[1,2,3,4,5,6,7];
const dayName=d=>new Date(2024,0,d).toLocaleDateString(S.settings.lang==='en'?'en-GB':S.settings.lang==='fr'?'fr-FR':'de-DE',{weekday:'long'});   // Jan 1 2024 is a Monday
export function availCard(){
  const list=Array.isArray(S.settings.avail)?S.settings.avail:[];
  return `<div class="card mt availcard"><h3 class="cardsub">${t('avail.title')}</h3>
    <p class="hint mb8">${t('avail.hint')}</p>
    <div class="availlist">${list.map((w,i)=>`<div class="availrow" data-i="${i}">
      <select class="select" data-av="d" aria-label="${esc(t('avail.day'))}">${DOW.map(d=>`<option value="${d}"${+w.d===d?' selected':''}>${esc(dayName(d))}</option>`).join('')}</select>
      <input class="input" type="time" step="900" data-av="from" value="${esc(w.from||'15:00')}" aria-label="${esc(t('avail.from'))}">
      <span class="muted">–</span>
      <input class="input" type="time" step="900" data-av="to" value="${esc(w.to||'18:00')}" aria-label="${esc(t('avail.to'))}">
      <button type="button" class="iconbtn" data-avdel aria-label="${esc(t('c.delete'))}">${IC.x}</button>
    </div>`).join('')||`<p class="muted emptyline">${t('avail.none')}</p>`}</div>
    <button type="button" class="btn small mt8" data-avadd>${IC.plus} ${t('avail.add')}</button>
  </div>`;
}
export function bindAvail(m){
  const card=m.querySelector('.availcard');if(!card)return;
  const save=()=>{persist();render()};
  const list=()=>{if(!Array.isArray(S.settings.avail))S.settings.avail=[];return S.settings.avail};
  card.querySelector('[data-avadd]').onclick=()=>{const l=list();const last=l[l.length-1];
    l.push(last?{d:last.d%7+1,from:last.from,to:last.to}:{d:1,from:'15:00',to:'18:00'});save()};
  card.querySelectorAll('.availrow').forEach(row=>{
    const i=+row.dataset.i;
    row.querySelectorAll('[data-av]').forEach(el=>el.onchange=()=>{
      const w=list()[i];if(!w)return;const k=el.dataset.av;
      w[k]=k==='d'?+el.value:el.value;
      if(w.to<=w.from){toast(t('avail.order'));}
      persist();
    });
    row.querySelector('[data-avdel]').onclick=()=>{list().splice(i,1);save()};
  });
}
